import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle2, 
  Clock,
  TrendingUp,
  Activity,
  Award,
  Calendar
} from 'lucide-react';
// Generated by Copilot — Migrated from Userfront to Clerk
import useAuthHeaders from '../hooks/useAuthHeaders';
import './Dashboard.css';

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const { userId, getToken } = useAuthHeaders();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/UsersRFC/${userId}/dashboard`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setMetrics(data);
        }
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRecentActivity = async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/UsersRFC/${userId}/actividad-reciente`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setRecentActivity(data);
        }
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      } finally {
        setActivityLoading(false);
      }
    };

    if (userId) {
      fetchMetrics();
      fetchRecentActivity();
    }
  }, [userId, getToken]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-MX', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatDateOnly = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/Mexico_City'
    }).format(date);
  };

  const calculateDaysRemaining = (endDateString) => {
    const endDate = new Date(endDateString);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor, linkTo, linkText }) => (
    <Card className="stat-card h-100 shadow-sm border-0">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <h6 className="stat-title text-muted mb-2">{title}</h6>
            <h2 className="stat-value mb-0">{value}</h2>
            {linkTo && linkText && (
              <Link to={linkTo} className="mt-2 d-inline-block" style={{ fontSize: '0.875rem', color: color, textDecoration: 'none', fontWeight: '500' }}>
                {linkText} →
              </Link>
            )}
          </div>
          <div 
            className="stat-icon-wrapper"
            style={{ 
              backgroundColor: bgColor,
              color: color 
            }}
          >
            <Icon size={24} strokeWidth={2} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const renderMetricsContent = () => {
    if (loading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" role="status" style={{ color: '#13a0ff' }}>
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="mt-3 text-muted">Cargando información...</p>
        </div>
      );
    }

    if (!metrics) {
      return (
        <div className="text-center p-5">
          <p className="text-muted">No se pudo cargar la información</p>
        </div>
      );
    }

    return (
      <Row className="g-4 mb-4">
        <Col md={6} xl={3}>
          <Card className="stat-card h-100 shadow-sm border-0">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="stat-title text-muted mb-2">Plan Actual</h6>
                  <h2 className="stat-value mb-0">{metrics.plan}</h2>
                  {metrics.plan.toLowerCase() === 'free' && metrics.currentPeriodStartUtc && metrics.currentPeriodEndUtc && (
                    <>
                      <div className="mt-2">
                        <div 
                          className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                          style={{ 
                            backgroundColor: 'rgba(255, 193, 7, 0.15)',
                            border: '1px solid rgba(255, 193, 7, 0.3)'
                          }}
                        >
                          <Clock size={14} style={{ color: '#ffc107' }} />
                          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#856404' }}>
                            {calculateDaysRemaining(metrics.currentPeriodEndUtc)} días restantes
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 pt-2" style={{ borderTop: '1px solid rgba(19, 160, 255, 0.15)' }}>
                        <div className="d-flex align-items-start gap-2">
                          <Calendar size={14} style={{ color: '#13a0ff', marginTop: '2px', flexShrink: 0 }} />
                          <div style={{ fontSize: '0.75rem', lineHeight: '1.5' }}>
                            <div style={{ color: '#6c757d', fontWeight: '500', marginBottom: '2px' }}>Período de facturación</div>
                            <div style={{ color: '#495057', fontWeight: '400' }}>
                              {formatDateOnly(metrics.currentPeriodStartUtc)}
                            </div>
                            <div style={{ color: '#adb5bd', fontSize: '0.7rem' }}>hasta</div>
                            <div style={{ color: '#495057', fontWeight: '400' }}>
                              {formatDateOnly(metrics.currentPeriodEndUtc)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div 
                  className="stat-icon-wrapper"
                  style={{ 
                    backgroundColor: "rgba(19, 160, 255, 0.1)",
                    color: "#13a0ff"
                  }}
                >
                  <Award size={24} strokeWidth={2} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} xl={3}>
          <StatCard
            title="RFCs Permitidos"
            value={metrics.rfcLimit}
            icon={FileText}
            color="#00d4aa"
            bgColor="rgba(0, 212, 170, 0.1)"
          />
        </Col>
        <Col md={6} xl={3}>
          <StatCard
            title="RFCs Usados"
            value={metrics.countRFC}
            icon={CheckCircle2}
            color="#ffc107"
            bgColor="rgba(255, 193, 7, 0.1)"
          />
        </Col>
        <Col md={6} xl={3}>
          <StatCard
            title="RFCs Disponibles"
            value={metrics.rfcLimit - metrics.countRFC}
            icon={Clock}
            color="#6c757d"
            bgColor="rgba(108, 117, 125, 0.1)"
            linkTo={metrics.rfcLimit - metrics.countRFC > 0 ? "/listado-rfc" : null}
            linkText={metrics.rfcLimit - metrics.countRFC > 0 ? "Agregar RFC" : null}
          />
        </Col>
      </Row>
    );
  };

  return (
    <div className="dashboard-container">
      <div className="welcome-banner mb-4">
        <div className="d-flex align-items-center gap-3">
          <div className="banner-icon">
            <Activity size={24} />
          </div>
          <div>
            <h5 className="mb-1">¡Bienvenido a SatGO!</h5>
            <p className="mb-0 text-muted">Gestiona todos tus RFCs de manera eficiente</p>
          </div>
        </div>
      </div>
      
      {renderMetricsContent()}

      <Row className="g-4">
        <Col md={12}>
          <Card className="activity-card shadow-sm border-0">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h5 className="card-title mb-0">Actividad Reciente</h5>
                <TrendingUp size={20} className="text-muted" />
              </div>
              {activityLoading && (
                <div className="text-center p-3">
                  <Spinner animation="border" size="sm" style={{ color: '#13a0ff' }}>
                    <span className="visually-hidden">Cargando...</span>
                  </Spinner>
                </div>
              )}
              {!activityLoading && recentActivity.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>RFC</th>
                        <th>Operación</th>
                        <th>Resultado</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentActivity.map((activity, index) => (
                        <tr key={`${activity.createdAt}-${index}`}>
                          <td className="fw-semibold">{activity.rfc ?? '—'}</td>
                          <td><code>{activity.operation}</code></td>
                          <td>
                            {/* Solo 'Ok'/'Error' son resultados; sin el campo (API anterior) no se afirma nada. */}
                            {activity.resultado === 'Ok' && <Badge bg="success">Exitosa</Badge>}
                            {activity.resultado === 'Error' && <Badge bg="danger">Con error</Badge>}
                            {activity.resultado !== 'Ok' && activity.resultado !== 'Error' && <span className="text-muted">—</span>}
                          </td>
                          <td className="text-muted">{formatDate(activity.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!activityLoading && recentActivity.length === 0 && (
                <p className="text-muted mb-0">No hay actividad reciente</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
